import { QueryClient, useQueries, useQuery } from '@tanstack/react-query'
import { useCurrentClient } from '@mysten/dapp-kit-react'
import * as batshit from '@yornaath/batshit'
import { memoize } from 'lodash-es'

import { ClientWithCoreApi } from '@mysten/sui/client'
import { windowedFiniteBatchScheduler } from '@yornaath/batshit'

export type UseObjectParams<T> = {
  id: string
  fromBcs: (bcs: Uint8Array) => T
  options?: { refetchInterval?: number }
}

const batcher = memoize((client: ClientWithCoreApi) => {
  return batshit.create({
    fetcher: async (objectIds: string[]) => {
      const { objects } = await client.core.getObjects({
        objectIds,
        include: { content: true },
      })

      return objects.map((o, i) => {
        const id = objectIds[i]
        if (o instanceof Error) {
          throw new Error(`Error fetching object ${id}: ${o.message}`)
        }
        if (!o.content) {
          throw new Error(`No bcs data returned for object ${id}`)
        }
        // core content is the raw Move struct BCS (Uint8Array) — pass straight through.
        return { id, bcs: o.content }
      })
    },
    resolver: batshit.keyResolver('id'),
    scheduler: windowedFiniteBatchScheduler({
      windowMs: 10,
      maxBatchSize: 50,
    }),
  })
})

export function useObjectBcs(objectId?: string, options?: { refetchInterval?: number }) {
  const client = useCurrentClient()

  const res = useQuery({
    queryKey: ['sui-object-bcs', objectId],
    queryFn: async () => {
      if (!objectId) {
        throw new Error('invariant violation')
      }
      return await batcher(client).fetch(objectId)
    },
    enabled: !!objectId,
    refetchInterval: options?.refetchInterval,
  })

  return {
    ...res,
    data: res.data?.bcs,
  }
}

export function useObjectBcsBatch(
  objectIds: string[],
  options?: { refetchInterval?: number; enabled?: boolean }
) {
  const client = useCurrentClient()

  // Deduplicate object IDs while preserving order
  const uniqueIds = [...new Set(objectIds)]

  // Create a map to track the original index of each ID
  const idToOriginalIndices = new Map<string, number[]>()
  objectIds.forEach((id, index) => {
    if (!idToOriginalIndices.has(id)) {
      idToOriginalIndices.set(id, [])
    }
    idToOriginalIndices.get(id)!.push(index)
  })

  const uniqueQueries = useQueries({
    queries: uniqueIds.map(id => ({
      queryKey: ['sui-object-bcs', id],
      queryFn: async () => {
        return await batcher(client).fetch(id)
      },
      refetchInterval: options?.refetchInterval,
      enabled: options?.enabled,
    })),
  })

  // Map the unique query results back to the original array structure
  const uniqueIdToQueryIndex = new Map<string, number>()
  uniqueIds.forEach((id, index) => {
    uniqueIdToQueryIndex.set(id, index)
  })

  return objectIds.map(id => {
    const queryIndex = uniqueIdToQueryIndex.get(id)!
    return uniqueQueries[queryIndex]
  })
}

export function useObject<T>(params: UseObjectParams<T>) {
  const bcsQuery = useObjectBcs(params.id, {
    refetchInterval: params.options?.refetchInterval,
  })

  return {
    ...bcsQuery,
    data: bcsQuery.data ? params.fromBcs(bcsQuery.data) : undefined,
  }
}

export function useObjectDataBatch<T>(
  params: UseObjectParams<T>[],
  options?: { refetchInterval?: number; enabled?: boolean }
) {
  const bcsQueries = useObjectBcsBatch(
    params.map(param => param.id),
    { refetchInterval: options?.refetchInterval, enabled: options?.enabled }
  )

  const fromBcsMap = new Map<string, (bcs: Uint8Array) => T>()
  for (const param of params) {
    fromBcsMap.set(param.id, param.fromBcs)
  }

  return bcsQueries.map(query => {
    const fromBcs = fromBcsMap.get(params[bcsQueries.indexOf(query)].id)!
    return {
      ...query,
      data: query.data?.bcs ? fromBcs(query.data.bcs) : undefined,
    }
  })
}

export async function refetchObjectData(
  queryClient: QueryClient,
  objectId: string,
  invalidateOnly = false
) {
  if (invalidateOnly) {
    await queryClient.invalidateQueries({
      queryKey: ['sui-object-bcs', objectId],
    })
  } else {
    await queryClient.refetchQueries({
      queryKey: ['sui-object-bcs', objectId],
    })
  }
}

export async function refetchObjectDataBatch(
  queryClient: QueryClient,
  objectIds: string[],
  invalidateOnly = false
) {
  if (invalidateOnly) {
    await Promise.all(
      objectIds.map(id =>
        queryClient.invalidateQueries({
          queryKey: ['sui-object-bcs', id],
        })
      )
    )
  } else {
    await Promise.all(
      objectIds.map(id =>
        queryClient.refetchQueries({
          queryKey: ['sui-object-bcs', id],
        })
      )
    )
  }
}
